"use client";

import { useState } from "react";
import { type CentralIconName } from "@/types/central-icon";
import { AnimatePresence, motion } from "framer-motion";
import useIsMobile from "@/hooks/use-is-mobile";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { CandidateReviewContactItem } from "./item";
import { CandidateContactRevealTrigger } from "../../reveal-trigger";
import type { ContactItem } from "../../item";
import { Button, SubmitButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { showToast } from "@/components/ui/sonner";
import { Icon } from "@/components/ui/icon";
import {
  DEFAULT_MANUAL_CONTACT_PHONE_COUNTRY,
  normalizeManualContactEmail,
  normalizeManualContactPhone,
  validateManualContactEmail,
  validateManualContactPhone,
} from "@/features/candidate/lib/manual-contact-validation";
import { ApiError, getApiErrorMessage } from "@/lib/api/error";

export type ContactReviewData = {
  revealed: boolean;
  local: {
    emails: ContactItem[];
    phoneNumbers: ContactItem[];
  };
  remote: {
    personalEmails: ContactItem[];
    professionalEmails: ContactItem[];
    phoneNumbers: ContactItem[];
  };
};

type ContactReviewActionProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactData?: ContactReviewData;
  onReveal?: () => void;
  onAddEmail?: (email: string) => Promise<void> | void;
  onAddPhone?: (phone: string) => Promise<void> | void;
  onRefresh?: () => Promise<void> | void;
  refreshing?: boolean;
  onFeedback?: (contactId: string, feedback: "up" | "down") => void;
  onDeleteContact?: (contactId: string) => Promise<void> | void;
  profileId?: string | null;
  /** Controls the reveal CTA wording: "reveal" (search) vs "enrich" (import). */
  revealAction?: "reveal" | "enrich";
};

type InlineMode = "none" | "email" | "phone";

function ContactSection({
  icon,
  label,
  contacts,
  onFeedback,
  onDelete,
}: {
  icon: CentralIconName;
  label: string;
  contacts: ContactItem[];
  onFeedback?: (contactId: string, feedback: "up" | "down") => void;
  onDelete?: (contactId: string) => void;
}) {
  if (contacts.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <span className="inline-flex items-center gap-1 text-sm font-medium">
        <Icon name={icon} size={16} />
        {label}
      </span>
      {contacts.map((item) => (
        <CandidateReviewContactItem
          key={item.id}
          contact={item}
          onFeedback={onFeedback}
          onDelete={item.isLocal ? onDelete : undefined}
        />
      ))}
    </div>
  );
}

// File-local; distinct copy from the shared @/components/ui/empty-state (ARBI-851).
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10">
      <div className="flex size-12 items-center justify-center rounded-full bg-contrast/4">
        <Icon
          name="IconContacts"
          size={24}
          className="text-foreground opacity-30"
        />
      </div>
      <div className="flex flex-col items-center gap-1">
        <p className="text-sm font-medium text-foreground/70">
          No contact information found
        </p>
        <p className="text-xs text-foreground/40">
          Add contacts manually or refresh to check for new data.
        </p>
      </div>
    </div>
  );
}

const INLINE_SPRING = {
  type: "spring" as const,
  damping: 28,
  stiffness: 400,
  mass: 0.5,
};

function InlineAddForm({
  mode,
  onSubmit,
  onCancel,
}: {
  mode: "email" | "phone";
  onSubmit: (value: string) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();

    const validationError =
      mode === "email"
        ? validateManualContactEmail(value)
        : validateManualContactPhone(value);
    if (validationError) {
      setError(validationError);
      return;
    }

    const normalized =
      mode === "email"
        ? normalizeManualContactEmail(value)
        : normalizeManualContactPhone(value);

    try {
      setLoading(true);
      await onSubmit(normalized);
      showToast.success(
        mode === "email" ? "Email added" : "Phone number added",
      );
      setValue("");
      setError(null);
      onCancel();
    } catch (err) {
      const backendMessage =
        err instanceof ApiError && err.status === 422 ? err.message : null;
      if (backendMessage) {
        setError(backendMessage);
      }
      showToast.error(
        mode === "email" ? "Could not add email" : "Could not add phone number",
        { description: getApiErrorMessage(err) },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={INLINE_SPRING}
      className="overflow-hidden"
    >
      <form
        className="flex flex-col gap-3 bg-surface-header px-6 pb-6 pt-0"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="h-px bg-contrast/15" />
        <label
          htmlFor={
            mode === "email" ? "manual-contact-email" : "manual-contact-phone"
          }
          className="text-sm font-medium"
        >
          {mode === "email" ? "Email Address" : "Phone Number"}
        </label>
        {mode === "email" ? (
          <Input
            id="manual-contact-email"
            className="text-base sm:text-sm"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="name@example.com"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onCancel();
            }}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "manual-contact-email-error" : undefined}
            autoFocus
          />
        ) : (
          <PhoneInput
            defaultCountry={DEFAULT_MANUAL_CONTACT_PHONE_COUNTRY}
            value={value || undefined}
            onChange={(next) => {
              setValue(next ?? "");
              if (error) setError(null);
            }}
            numberInputProps={{
              id: "manual-contact-phone",
              "aria-invalid": error ? true : undefined,
              "aria-describedby": error
                ? "manual-contact-phone-error"
                : undefined,
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Escape") onCancel();
              },
              autoFocus: true,
            }}
          />
        )}
        {error ? (
          <p
            id={
              mode === "email"
                ? "manual-contact-email-error"
                : "manual-contact-phone-error"
            }
            className="text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
            className="h-9 sm:h-7"
          >
            Cancel
          </Button>
          <SubmitButton
            type="submit"
            variant="primary"
            size="sm"
            loading={loading}
            loadingLabel="Adding"
            disabled={!value.trim()}
            className="h-9 sm:h-7"
          >
            Add
          </SubmitButton>
        </div>
      </form>
    </motion.div>
  );
}

/** Mobile: adding a contact opens a focused nested drawer over the dimmed list
 *  instead of the inline expand-form (cramped behind the keyboard on phones). */
function AddContactModal({
  mode,
  onSubmit,
  onClose,
}: {
  mode: "email" | "phone";
  onSubmit: (value: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!value.trim()) return;
    try {
      setLoading(true);
      await onSubmit(value.trim());
      showToast.success(
        mode === "email" ? "Email added" : "Phone number added",
      );
      onClose();
    } catch {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(next) => (!next && !loading ? onClose() : undefined)}
      dismissible={!loading}
    >
      <ModalContent size="sm">
        <ModalHeader
          title={mode === "email" ? "Add Email Address" : "Add Phone Number"}
        />
        <ModalBody className="flex flex-col gap-3">
          <span className="text-sm font-medium">
            {mode === "email" ? "Email Address" : "Phone Number"}
          </span>
          <Input
            className="text-base sm:text-sm"
            placeholder={
              mode === "email" ? "name@example.com" : "+1 (234) 567-8901"
            }
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
            autoFocus
          />
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <SubmitButton
            type="submit"
            variant="primary"
            size="sm"
            loading={loading}
            loadingLabel="Adding"
            disabled={!value.trim()}
          >
            Add
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function ContactReviewAction({
  open,
  onOpenChange,
  contactData,
  onReveal,
  onAddEmail,
  onAddPhone,
  onRefresh,
  refreshing,
  onFeedback,
  onDeleteContact,
  revealAction = "reveal",
}: ContactReviewActionProps) {
  const isMobile = useIsMobile();
  const [inlineMode, setInlineMode] = useState<InlineMode>("none");
  const [pendingDelete, setPendingDelete] = useState<ContactItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setInlineMode("none");
      if (!deleting) setPendingDelete(null);
    }
    onOpenChange(next);
  };

  const handleRefresh = async () => {
    try {
      await onRefresh?.();
      showToast.success("Contact data refreshed");
    } catch (err) {
      showToast.error("Failed to refresh contact data", {
        description: getApiErrorMessage(err),
      });
    }
  };

  const requestDelete = (contactId: string) => {
    const contact =
      [
        ...(contactData?.local.emails ?? []),
        ...(contactData?.local.phoneNumbers ?? []),
      ].find((c) => c.id === contactId) ?? null;
    if (contact) setPendingDelete(contact);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      await onDeleteContact?.(pendingDelete.id);
      showToast.success("Contact removed");
      setPendingDelete(null);
    } catch (err) {
      showToast.error("Failed to remove contact", {
        description: getApiErrorMessage(err),
      });
    } finally {
      setDeleting(false);
    }
  };

  const hasRevealed = contactData?.revealed;
  const hasRemoteData =
    (contactData?.remote.personalEmails.length ?? 0) > 0 ||
    (contactData?.remote.professionalEmails.length ?? 0) > 0 ||
    (contactData?.remote.phoneNumbers.length ?? 0) > 0;
  const hasLocalData =
    (contactData?.local.emails.length ?? 0) > 0 ||
    (contactData?.local.phoneNumbers.length ?? 0) > 0;
  const hasAnyData = hasRemoteData || hasLocalData;

  return (
    <>
      <Modal open={open} onOpenChange={handleOpenChange}>
        <ModalContent className="flex flex-col gap-0">
          <ModalHeader title="Available Contact Information" />

          {contactData ? (
            <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto p-6 sm:max-h-[50vh]">
              {!hasRevealed && (
                <CandidateContactRevealTrigger
                  onClick={onReveal}
                  action={revealAction}
                  className="h-10 sm:h-10 text-sm"
                />
              )}

              {!hasAnyData && hasRevealed && <EmptyState />}

              <ContactSection
                icon="IconEmail1"
                label="Personal Emails"
                contacts={contactData.remote.personalEmails}
                onFeedback={onFeedback}
              />
              <ContactSection
                icon="IconEmail1Sparkle"
                label="Professional Emails"
                contacts={contactData.remote.professionalEmails}
                onFeedback={onFeedback}
              />
              <ContactSection
                icon="IconPhoneDynamicIsland"
                label="Phone Numbers"
                contacts={contactData.remote.phoneNumbers}
                onFeedback={onFeedback}
              />
              <ContactSection
                icon="IconEmail1"
                label="Manually Added Emails"
                contacts={contactData.local.emails}
                onFeedback={onFeedback}
                onDelete={requestDelete}
              />
              <ContactSection
                icon="IconPhoneDynamicIsland"
                label="Manually Added Phone Numbers"
                contacts={contactData.local.phoneNumbers}
                onFeedback={onFeedback}
                onDelete={requestDelete}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center p-12">
              <Icon
                name="IconLoadingCircle"
                size={20}
                className="animate-spin text-foreground opacity-30"
              />
            </div>
          )}

          {contactData && (
            <div className="flex flex-col gap-2 border-t border-border bg-surface-header px-6 py-3 sm:flex-row sm:items-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setInlineMode(inlineMode === "email" ? "none" : "email")
                }
                className="h-9 w-full justify-center sm:h-7 sm:w-auto"
              >
                <Icon name="IconEmail1" size={14} />
                Add Email
                <Icon name="IconPlusSmall" size={14} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setInlineMode(inlineMode === "phone" ? "none" : "phone")
                }
                className="h-9 w-full justify-center sm:h-7 sm:w-auto"
              >
                <Icon name="IconPhoneDynamicIsland" size={14} />
                Add Phone
                <Icon name="IconPlusSmall" size={14} />
              </Button>
              {hasRevealed && onRefresh && (
                <SubmitButton
                  variant="secondary"
                  size="sm"
                  onClick={handleRefresh}
                  loading={refreshing}
                  loadingLabel="Refreshing"
                  className="h-9 w-full justify-center opacity-60 sm:h-7 sm:w-auto"
                >
                  <Icon name="IconArrowUpCircle" size={14} />
                  Refresh Contact Info
                </SubmitButton>
              )}
            </div>
          )}

          {/* Desktop: inline add form below footer */}
          {!isMobile && (
            <AnimatePresence>
              {inlineMode === "email" && onAddEmail && (
                <InlineAddForm
                  key="email"
                  mode="email"
                  onSubmit={onAddEmail}
                  onCancel={() => setInlineMode("none")}
                />
              )}
              {inlineMode === "phone" && onAddPhone && (
                <InlineAddForm
                  key="phone"
                  mode="phone"
                  onSubmit={onAddPhone}
                  onCancel={() => setInlineMode("none")}
                />
              )}
            </AnimatePresence>
          )}
        </ModalContent>
      </Modal>

      {/* Mobile: adding a contact opens a focused drawer over the dimmed list */}
      {isMobile && inlineMode === "email" && onAddEmail && (
        <AddContactModal
          mode="email"
          onSubmit={onAddEmail}
          onClose={() => setInlineMode("none")}
        />
      )}
      {isMobile && inlineMode === "phone" && onAddPhone && (
        <AddContactModal
          mode="phone"
          onSubmit={onAddPhone}
          onClose={() => setInlineMode("none")}
        />
      )}

      <Modal
        open={pendingDelete !== null}
        onOpenChange={(next) => (!next ? closeDeleteConfirm() : undefined)}
        dismissible={!deleting}
      >
        <ModalContent size="sm">
          <ModalHeader title="Delete contact?" iconName="IconTrashRounded" />
          <ModalBody>
            <p className="text-sm text-muted-foreground">
              {pendingDelete?.type === "phone"
                ? "This phone number"
                : "This email address"}{" "}
              (
              <span className="font-medium text-heading">
                {pendingDelete?.value}
              </span>
              ) will be removed from this candidate&apos;s contact information.
              This can&apos;t be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              size="sm"
              onClick={closeDeleteConfirm}
              disabled={deleting}
            >
              Cancel
            </Button>
            <SubmitButton
              type="button"
              variant="destructive"
              size="sm"
              loading={deleting}
              loadingLabel="Deleting"
              onClick={handleConfirmDelete}
            >
              Delete contact
            </SubmitButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
